import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button, Dropdown, Icon, Popup } from 'semantic-ui-react';
import { Uploader, UploadField } from '@navjobs/upload';
import _ from 'lodash';

import { API } from '../../../shared/constants';
import { getAuth } from '../../../shared/lib/request';
import AllegatoService from '../../../shared/services/api/allegato';
import GeneraDocumentoService from '../../../shared/services/rpc/genera-documento';

const StyledContainer = styled.div`
  position: relative;
  z-index: 3;
  & .dropdown > .icon {
    padding: 18px 17px;
    position: relative;
    left: -5px;
    top: -7px;
  }
  & i.dropdown.icon {
    margin: 0 0 0 0;
    padding: 0;
    margin-left: 15px;
    position: relative;
    margin-top: 9px;
    top: 2px;
  }
`;

const ActionButton = styled(Button)`
  border-radius: 10em !important;
  color: #fff !important;
  padding: 0 !important;
  width: 36px;
  position: relative;
  border: 0px solid;

  &:active {
    background-color: #cacbcd !important;
  }
`;

class ImpresaUploadDocument extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: null,
      isLoading: false,
      modal: false,
      files: null,
      formData: {},
    };
  }

  /** Call on upload complete */
  onUploadComplete = () => this.setState({ isLoading: false }, () => this.props.loadDocuments());

  /** Call on upload error */
  onUploadError = (error) => {
    if (error) {
      this.setState({ isLoading: false });
      if (!this.props.sbloccoGenerazioneContratto) {
        this.props.notification().addNotification({
          message: 'Impossibile caricare il documento, attendere lo sblocco della generazione contratto.',
          level: 'error',
        });
      } else {
        this.props.notification().addNotification({
          message:
            "Si è verificato un errore durante l'upload del documento. Se il problema persiste contattare l'assistenza tecnica.",
          level: 'error',
        });
      }
    }
  };

  /** If document is identity, show form, otherwise upload document */
  beforeSend = (onFiles, files) => this.setState({ isLoading: true }, () => onFiles(files));

  /** Close modal */
  closeModal = () => this.setState({ modal: false });

  /** Download document */
  download = (versionId = null) => {
    AllegatoService.getFileById(this.props.documentId, this.props.api, versionId)
      .then((res) => {
        const type = res.headers['content-type'];

        const file = new Blob([res.data], { type });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      })
      .catch(() =>
        this.props.notification().addNotification({
          message:
            "Si è verificato un errore durante il download del documento. Se il problema persiste contattare l'assistenza tecnica.",
          level: 'error',
        })
      );
  };

  /** Download specific version of document */
  downloadVersion = (event, versionId) => {
    event.preventDefault();
    event.stopPropagation();

    // Download documents
    this.download(versionId);
  };

  /** Generate document */
  generate = () => {
    this.setState({ isLoading: true });

    GeneraDocumentoService.getDocumento(this.props.generate, null, null, null, null, null, this.props.impresaId)
      .then((res) => {
        this.setState({ isLoading: false });

        const type = res.headers['content-type'];

        const file = new Blob([res.data], { type });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL);
      })
      .catch((err) => {
        this.setState({ isLoading: false });

        const reader = new FileReader();
        reader.onload = () => {
          const error = JSON.parse(reader.result);
          this.props.notification().addNotification({
            message:
              error.detail || "Si è verificato un errore. Se il problema persiste contattare l'assistenza tecnica.",
            level: 'error',
          });
        };
        reader.readAsText(err.data);
      });
  };

  render() {
    const { isLoading, formData } = this.state;
    const { documentId, api, versions, impresaId, generate, disabled, disabledTooltip } = this.props;

    return (
      <Uploader
        request={{
          fileName: 'file',
          url: documentId ? `${API.BASEURL}${api}/${documentId}` : `${API.BASEURL}${api}`,
          method: documentId ? 'PUT' : 'POST',
          fields: {
            ...formData,
            processoImprese: impresaId,
          },
          headers: {
            Authorization: getAuth(),
          },
          withCredentials: false,
        }}
        onError={this.onUploadError}
        onComplete={this.onUploadComplete}
        uploadOnSelection
        reset
      >
        {({ onFiles, progress }) => {
          const actionButton = (
            <ActionButton as="div" key="upload-actions" loading={isLoading} disabled={disabled}>
              <StyledContainer>
                <Dropdown icon="setting" floating disabled={disabled}>
                  <Dropdown.Menu style={{ right: 0, left: 'auto' }}>
                    <UploadField
                      onFiles={(files) =>
                        !disabled && this.setState({ isLoading: true }, this.beforeSend(onFiles, files))
                      }
                      containerProps={{
                        style: {
                          overflow: 'hidden',
                          width: '100%',
                          position: 'relative',
                          display: 'inline-block',
                          pointerEvents: disabled ? 'none' : 'auto',
                          zIndex: 3,
                        },
                        className: 'uploadHoverable',
                      }}
                    >
                      <Dropdown.Item
                        disabled={disabled}
                        className="uploadable-dropdown-item"
                        style={{
                          padding: '.78571429rem 1.14285714rem',
                          color: disabled ? 'rgba(0,0,0,.4)' : 'rgba(0,0,0,.87)',
                          fontWeight: 200,
                        }}
                      >
                        <Icon
                          loading={isLoading}
                          name={isLoading ? 'circle notched' : 'upload'}
                          style={{ marginRight: '.78571429rem' }}
                        />
                        Carica
                      </Dropdown.Item>
                    </UploadField>

                    {generate && (
                      <Dropdown.Item key={`generate${documentId}`} onClick={() => !disabled && this.generate()}>
                        <Icon name="download" />
                        Genera
                      </Dropdown.Item>
                    )}

                    {documentId && (
                      <Dropdown.Item key={`download${documentId}`} onClick={() => this.download()}>
                        <Icon name="download" />
                        Scarica
                      </Dropdown.Item>
                    )}
                    {!_.isEmpty(versions) && (
                      <Dropdown.Item className="noPaddingDropdown">
                        <Dropdown text="Versioni" style={{ padding: '.78571429rem 1.14285714rem!important' }}>
                          <Dropdown.Menu style={{ right: '0', left: 'auto' }}>
                            {versions.map((version) => (
                              <Dropdown.Item
                                style={{ borderBottom: '1px solid #ccc' }}
                                key={version.id}
                                onClick={(event) => this.downloadVersion(event, version.id)}
                              >
                                <div>{version.name}</div>
                                <div style={{ fontSize: 10, color: '#333d68' }}>{version.date}</div>
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </StyledContainer>
            </ActionButton>
          );

          return (
            <div>
              {isLoading && (
                <div
                  className="upload-document-progress"
                  style={{ height: `${progress || 0}%`, left: 0, right: 0, bottom: 0 }}
                  key="upload-progress"
                />
              )}
              {disabled && disabledTooltip ? (
                <Popup
                  trigger={<div style={{ display: 'inline-block' }}>{actionButton}</div>}
                  content={disabledTooltip}
                  inverted
                  style={{ backgroundColor: '#333d68' }}
                />
              ) : (
                actionButton
              )}
            </div>
          );
        }}
      </Uploader>
    );
  }
}

ImpresaUploadDocument.propTypes = {
  /** Id of document */
  documentId: PropTypes.string,
  /** Api of document */
  api: PropTypes.string,
  /** Array of versions of file */
  versions: PropTypes.array,
  /** Id of impresa */
  impresaId: PropTypes.string,
  /** Flag sblocco generazione contratto */
  sbloccoGenerazioneContratto: PropTypes.bool,
  /** Method to generate document */
  generate: PropTypes.string,
  /** Function to load documents */
  loadDocuments: PropTypes.func,
  /** Function for call the notification system */
  notification: PropTypes.func,
  /** Disabled flag */
  disabled: PropTypes.bool,
  /** Disabled tooltip message */
  disabledTooltip: PropTypes.string,
};

export default ImpresaUploadDocument;



// WEBPACK FOOTER //
// ./app/components/ProcessoImpresa/ImpresaUploadDocument/index.js